import React, { useState } from "react";

import { siteLocationPrefix } from "../../constants";
import * as styles from "./TestStyle.module.css";

type MatcherField = Readonly<{
  field: string;
  displayName?: string;
  currentName?: string;
  candidateCount: number;
  decision: string;
  suggestedName?: string;
  suggestedFromOtherAccount: boolean;
  confidence?: number;
  evidence?: string;
  reason?: string;
}>;

type MatcherResult = Readonly<{
  decision: string;
  fields: MatcherField[];
  model?: string;
  totalMs: number;
  modelMs: number;
  inputTokens?: number;
  outputTokens?: number;
  imageCount: number;
}>;

type MatcherRun = Readonly<{ caseId: string; ranAt: string; status: number; body: string }>;

// Prefer a CRM record URL's `id=` over earlier GUIDs like `appid=`.
const guidPattern = /(?:[?&]id=)?([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;

const decisionClass: Partial<Record<string, string>> = {
  AutoLink: styles.decisionAutoLink,
  Suggest: styles.decisionSuggest,
};

const parseResult = (body: string): MatcherResult | undefined => {
  try {
    const parsed = JSON.parse(body) as MatcherResult;
    return Array.isArray(parsed.fields) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const prettyPrint = (body: string) => {
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

export const TestCaseMatcher = () => {
  const [input, setInput] = useState("");
  const [crossAccount, setCrossAccount] = useState(false);
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState<MatcherRun>();
  const matches = [...input.matchAll(guidPattern)];
  const caseId = (matches.find(match => /id=/i.test(match[0])) ?? matches[0])?.[1];

  const runMatcher = async () => {
    setRunning(true);
    try {
      const response = await fetch(`${siteLocationPrefix}/test/case-matcher`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId, crossAccount }),
      });
      setRun(
        response.ok
          ? ((await response.json()) as MatcherRun)
          : {
              caseId: caseId ?? "",
              ranAt: new Date().toISOString(),
              status: response.status,
              body: await response.text(),
            }
      );
    } catch (error) {
      setRun({
        caseId: caseId ?? "",
        ranAt: new Date().toISOString(),
        status: 0,
        body: (error as Error).message,
      });
    } finally {
      setRunning(false);
    }
  };

  const result = run && parseResult(run.body);

  return (
    <div className={`${styles.ticketingActionsWrapper} ${styles.caseMatcher}`}>
      <h3>Case matcher (dry run)</h3>
      <div className={styles.row}>
        <input
          className={styles.input}
          aria-label="Case ID or CRM URL"
          placeholder="Case ID or CRM URL"
          value={input}
          onChange={event => setInput(event.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={crossAccount}
            onChange={event => setCrossAccount(event.target.checked)}
          />{" "}
          Cross-account
        </label>
        <button
          className={styles.testButton}
          type="button"
          disabled={!caseId || running}
          onClick={runMatcher}
        >
          {running ? "Running..." : "Run"}
        </button>
      </div>
      {run && (
        <div aria-live="polite">
          <p className={styles.matcherMeta}>
            HTTP {run.status} · {run.caseId} · {new Date(run.ranAt).toLocaleTimeString()}
            {result && (
              <>
                {" "}
                · <strong className={decisionClass[result.decision]}>
                  {result.decision}
                </strong> · {result.totalMs} ms ({result.modelMs} ms model) ·{" "}
                {result.inputTokens ?? "?"}/{result.outputTokens ?? "?"} tokens ·{" "}
                {result.imageCount} images · {result.model ?? "no model"}
              </>
            )}
          </p>
          {result && (
            <table className={styles.matcherTable}>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Current</th>
                  <th>Decision</th>
                  <th>Suggested</th>
                  <th>Confidence</th>
                  <th>Evidence / reason</th>
                </tr>
              </thead>
              <tbody>
                {result.fields.map(field => (
                  <tr key={field.field}>
                    <td>
                      {field.displayName ?? field.field}
                      <br />
                      <small>{field.candidateCount} candidates</small>
                    </td>
                    <td>{field.currentName ?? "-"}</td>
                    <td className={decisionClass[field.decision]}>{field.decision}</td>
                    <td>
                      {field.suggestedName ?? "-"}
                      {field.suggestedFromOtherAccount && (
                        <>
                          <br />
                          <small>other account</small>
                        </>
                      )}
                    </td>
                    <td>
                      {field.confidence === undefined || field.confidence === null
                        ? "-"
                        : `${Math.round(field.confidence * 100)}%`}
                    </td>
                    <td>
                      {field.evidence && <q>{field.evidence}</q>}
                      {field.reason && <p>{field.reason}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <details open={!result}>
            <summary>Raw response</summary>
            <pre className={styles.matcherRaw}>{prettyPrint(run.body)}</pre>
          </details>
        </div>
      )}
    </div>
  );
};
